import { useCallback, useMemo, useState } from 'react';
import { FormattedDate, FormattedTime } from 'react-intl';

import { useEstimationCostQueryActions } from '@/api/hooks/estimationCostQueryActions';
import { useEstimationQueryActions } from '@/api/hooks/estimationQueryActions';
import type {
  EstimationCostResponse,
  ReviewCommentsSummaryGroupResponse,
  ReviewCommentsSummaryResponse,
} from '@/api/schema/data-contracts';
import { ApiEstimationStage } from '@/api/schema/data-contracts';
import IconClock from '@/assets/Icons/IconClock';
import IconUser from '@/assets/Icons/IconUser';
import { AssumptionsModal } from '@/components/Assumption';
import { SanitizedHtml } from '@/components/atoms/SanitizedHtml';
import { Stack } from '@/components/atoms/Stack';
import TableSkeleton from '@/components/atoms/TableSkeleton';
import Search from '@/components/molecules/Search';
import { CommentsModal } from '@/components/organism/Comments/CommentsModal';
import { commentsMessageDefinitions } from '@/components/organism/Comments/messages';
import { ExportButtonsSelector } from '@/components/organism/DataTable/components/exportSelector';
import { eExportFormat } from '@/components/organism/DataTable/model';
import type { EstimationExportButtonsProps } from '@/components/organism/EstimationExportButtons';
import { messages as sidebarMessageDefinitions } from '@/components/organism/Sidebar/messages';
import { useMessages } from '@/hooks/messages';
import { useEstimationExport } from '@/hooks/useEstimationExport';
import EstimationItemDetailModal from '@/pages/estimation/detail/EstimationItemDetailModal';
import type { EstimationItemsGroupedTableProps } from '@/pages/estimation/detail/EstimationItemsGroupedTables';
import { Route } from '@/routes/estimation/detail/$estimationId/$tabId';
import type { OmitSafely } from '@/types';
import { sortResourceTypes } from '@/utils/sorting';

import { Layout } from '../../Layout';
import { getResourceEstimationItemDetail } from '../../mappers/estimationItemDetailMapper';
import { estimationTabMessageDefinitions } from '../../messages';
import type {
  CostModel,
  EstimationItemActionCallback,
  ResourceEstimationItemsTableRecord,
} from '../../model';
import { useResourceEstimationItemsTableRecords } from '../../useEstimationItemsTableRecords';
import SummaryTable from '../ResourceCost/SummaryTable';
import { TopLevelEstimationItemsForm } from './TopLevelEstimationItemsForm';

const emptyArray: Array<ResourceEstimationItemsTableRecord> = [];

const EstimationDetailReviewTab = () => {
  const { estimationId } = Route.useLoaderData();
  const [searchText, setSearchText] = useState('');
  const sidebarMessages = useMessages(sidebarMessageDefinitions);
  const tabMessages = useMessages(estimationTabMessageDefinitions);
  const commentsMessages = useMessages(commentsMessageDefinitions);

  const getContextTypeLabel = useCallback(
    (contextType: string) => {
      switch (contextType.toUpperCase()) {
        case 'ESTIMATION':
          return commentsMessages.contextTypeEstimationLabel();
        case 'RESOURCING':
          return commentsMessages.contextTypeResourcingLabel();
        case 'COSTING':
          return commentsMessages.contextTypeCostingLabel();
        default:
          return contextType;
      }
    },
    [commentsMessages],
  );

  const {
    isLoading,
    data,
    getResourceEstimationItemsTableRecordsByParent: getEstimationItemsTableRecordsByParent,
  } = useResourceEstimationItemsTableRecords(estimationId, { shouldFetchResources: true });

  const { useGetEstimationCosts } = useEstimationCostQueryActions();
  const { useGetReviewCommentsSummary } = useEstimationQueryActions();
  const { data: estimationCost, isLoading: isLoadingEstimationCost } = useGetEstimationCosts(
    estimationId,
  ) as { data: EstimationCostResponse | '' | undefined; isLoading: boolean };
  const stage = data?.estimation.stage;
  const showSummarySection =
    stage === ApiEstimationStage.IN_PROCESS || stage === ApiEstimationStage.APPROVED;
  const { data: reviewCommentsSummary, isLoading: isLoadingReviewSummary } =
    useGetReviewCommentsSummary(showSummarySection ? estimationId : undefined) as {
      data: ReviewCommentsSummaryResponse | undefined;
      isLoading: boolean;
    };

  const [estimationItemIdForAssumptionsModal, setEstimationItemIdForAssumptionsModal] =
    useState<string>();
  const [estimationItemIdForCommentsModal, setEstimationItemIdForCommentsModal] =
    useState<string>();
  const [estimationItemIdsForDetailsModal, setEstimationItemIdsForDetailsModal] = useState<
    string[]
  >([]);

  const topLevelEstimationItems = useMemo(
    () => getEstimationItemsTableRecordsByParent(undefined),
    [getEstimationItemsTableRecordsByParent],
  );

  const includedTopLevelEstimationItems = useMemo(
    () => topLevelEstimationItems.filter((item) => !item.isOmitted),
    [topLevelEstimationItems],
  );

  const calculatedResultByEstimationItemId = useMemo(
    () =>
      new Map<string, number>(
        data?.estimationItems.map((item) => [item.id, item.calculatedResult ?? 0]),
      ),
    [data?.estimationItems],
  );

  const costModel = useMemo<CostModel | undefined>(() => {
    if (estimationCost === undefined || data?.estimationModel.currencyIds === undefined) {
      return undefined;
    }

    if (estimationCost === '') {
      return {
        currencyId: data.estimationModel.currencyIds[0],
        resourceTypeCosts: sortResourceTypes(
          data.estimationModel.resourceTypes,
          data.estimationModel.resourceTypeGroups,
        ).map((resourceType) => ({
          resourceTypeId: resourceType.id,
          costTypeId: resourceType.costTypeId,
          price: 0,
        })),
      };
    }

    return {
      currencyId: estimationCost.currencyId,
      resourceTypeCosts: sortResourceTypes(
        data.estimationModel.resourceTypes,
        data.estimationModel.resourceTypeGroups,
      ).map((resourceType) => ({
        resourceTypeId: resourceType.id,
        costTypeId: resourceType.costTypeId,
        price:
          estimationCost.resourceTypeCosts.find(
            (resourceTypeCost) => resourceTypeCost.resourceTypeId === resourceType.id,
          )?.price ?? 0,
      })),
    };
  }, [estimationCost, data]);

  const exportProps = useMemo<OmitSafely<EstimationExportButtonsProps, 'parentEstimationItemId'>>(
    () => ({
      estimationId,
      costModel,
      title: `${sidebarMessages.detailedEstimation()} - ${tabMessages.reviewLabel()}`,
      outputOptionsArray: [
        {
          showResourcePurposeEstimationItems: true,
          showPhaseLeadColumn: true,
          showMethodologyColumn: true,
          showEstimateColumn: true,
          showCostSumColumn: true,
        },
      ],
    }),
    [estimationId, costModel, sidebarMessages, tabMessages],
  );

  const estimationExporter = useEstimationExport(exportProps);
  const handleExportButtonClick = useCallback(
    (format: eExportFormat) => estimationExporter(format, undefined),
    [estimationExporter],
  );

  const exportConfiguration = useMemo<eExportFormat[]>(
    () => [
      eExportFormat.EXCEL,
      eExportFormat.PDF,
      eExportFormat.CSV,
      eExportFormat.JSON,
      eExportFormat.PRINT,
    ],
    [],
  );

  if (isLoading || isLoadingEstimationCost) {
    return (
      <Layout>
        <TableSkeleton />
      </Layout>
    );
  }

  if (data === undefined || costModel === undefined) {
    return (
      <Layout>
        <TableSkeleton />
      </Layout>
    );
  }

  const onAssumptionsClicked: EstimationItemActionCallback = setEstimationItemIdForAssumptionsModal;
  const onCommentsClicked: EstimationItemActionCallback = setEstimationItemIdForCommentsModal;
  const onDetailsClicked: EstimationItemActionCallback = (estimationItemId: string) => {
    setEstimationItemIdsForDetailsModal([...estimationItemIdsForDetailsModal, estimationItemId]);
  };

  const tableProps: OmitSafely<
    EstimationItemsGroupedTableProps,
    'estimationItems' | 'control' | 'onIncludeClicked' | 'searchText' | 'formModelEstimationItems'
  > = {
    estimationModel: data.estimationModel,
    methodologies: data.methodologies,
    costs: costModel,
    estimationUnit: data.estimation.unit,
    showButtons: true,
    showMethodology: true,
    showResponsible: true,
    showParameters: false,
    isReadOnly: true,
    onAssumptionsClicked,
    onCommentsClicked,
    onDetailsClicked,
    calculatedResultByEstimationItemId,
    showEstimateColumn: true,
  };

  return (
    <Layout>
      <Stack gap={10}>
        {showSummarySection && !isLoadingReviewSummary && reviewCommentsSummary !== undefined && (
          <div className='dark:border-white-dark/20 dark:bg-c0 rounded-xl border border-gray-200 bg-white p-4'>
            <h3 className='mb-3 text-base font-semibold'>
              {tabMessages.reviewCommentsSummaryTitle()}
            </h3>
            {reviewCommentsSummary.hasComments === false ? (
              <p className='text-gray-500 dark:text-gray-400'>
                {tabMessages.reviewCommentsSummaryNotAvailable()}
              </p>
            ) : (
              <div className='space-y-3'>
                {reviewCommentsSummary.groups?.map((group: ReviewCommentsSummaryGroupResponse) => (
                  <div
                    key={group.estimationItemId ?? group.elementPath}
                    className='dark:bg-c16 rounded-lg bg-gray-50 p-4'
                  >
                    <div className='mb-3 flex items-center gap-2'>
                      <span className='font-medium text-gray-800 dark:text-gray-200'>
                        {group.elementPath}
                      </span>
                      {group.contextType && (
                        <span className='dark:border-white-dark/30 dark:text-white-dark rounded-md border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-semibold whitespace-nowrap text-gray-600 dark:bg-transparent'>
                          {getContextTypeLabel(group.contextType)}
                        </span>
                      )}
                    </div>
                    <div className='space-y-2'>
                      {group.comments.map((c) => (
                        <div
                          key={`${c.reviewerName}::${c.createdAt}`}
                          className='dark:bg-c4 flex gap-3 rounded-md bg-white p-3'
                        >
                          <div className='min-w-0 flex-1'>
                            <div className='text-sm text-gray-700 dark:text-white'>
                              <SanitizedHtml htmlString={c.text} />
                            </div>
                            <div className='dark:text-white-dark mt-1.5 flex flex-wrap items-center gap-3 text-xs text-gray-500'>
                              <span className='inline-flex items-center gap-1'>
                                <IconUser className='h-3 w-3' />
                                {c.reviewerName}
                              </span>
                              {c.createdAt && (
                                <span className='inline-flex items-center gap-1'>
                                  <IconClock className='h-3 w-3' />
                                  <FormattedDate
                                    value={c.createdAt}
                                    day='numeric'
                                    month='long'
                                    year='numeric'
                                  />
                                  {', '}
                                  <FormattedTime value={c.createdAt} />
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className='mt-4 flex justify-end gap-2 rounded-xl'>
          <Search handleOnChange={setSearchText} />
          <ExportButtonsSelector
            exportConfiguration={exportConfiguration}
            onExportButtonClick={handleExportButtonClick}
          />
        </div>
        {/* TODO: don't use the form from resource costs tab folder here. Instead, make it a common component and move it 2 folders higher. */}
        <SummaryTable
          showEstimateColumn={true}
          estimationItems={includedTopLevelEstimationItems}
          estimationModel={data.estimationModel}
          costs={costModel}
        />
        {/* TODO: don't use the form from estimation tab here */}
        <TopLevelEstimationItemsForm
          topLevelEstimationItems={topLevelEstimationItems}
          methodologies={data.methodologies}
          estimationMethodologyId={data.estimation.methodologyId}
          tableProps={{ ...tableProps, searchText }}
        />
      </Stack>
      {estimationItemIdsForDetailsModal.map((estimationItemIdForDetail) => (
        <EstimationItemDetailModal
          key={estimationItemIdForDetail}
          open={!!estimationItemIdForDetail}
          onClose={() =>
            setEstimationItemIdsForDetailsModal([...estimationItemIdsForDetailsModal.slice(0, -1)])
          }
          onSubmit={() => Promise.resolve()}
          shouldResetForm={true}
          onFormReset={() => {}}
          estimationItem={
            estimationItemIdForDetail === undefined
              ? undefined
              : getResourceEstimationItemDetail(estimationItemIdForDetail, data)
          }
          estimationItems={
            estimationItemIdForDetail
              ? getEstimationItemsTableRecordsByParent(estimationItemIdForDetail)
              : emptyArray
          }
          estimationMethodologyId={data.estimation.methodologyId}
          estimationCultureName={data.estimation.cultureName}
          exportProps={exportProps}
          {...tableProps}
        />
      ))}
      <AssumptionsModal
        estimationId={estimationId}
        onClose={() => setEstimationItemIdForAssumptionsModal(undefined)}
        estimationItem={data.estimationItems.find(
          (x) => x.id === estimationItemIdForAssumptionsModal,
        )}
        isReadOnlyForced
      />
      <CommentsModal
        commentType='ESTIMATION_ITEM'
        commentContext='ESTIMATION'
        size='s'
        estimationId={data.estimation.id}
        estimationItem={data.estimationItems.find((x) => x.id === estimationItemIdForCommentsModal)}
        onClose={() => setEstimationItemIdForCommentsModal(undefined)}
      />
    </Layout>
  );
};

export default EstimationDetailReviewTab;
